import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { FaPencilAlt, FaArrowLeft, FaBook, FaTrophy, FaLightbulb, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { QuizFeedbackBubble } from '../components/CharacterBubble';
import { useRandomCharacter } from '../components/CharacterBubble';

function GrammarPage() {
  const [mode, setMode] = useState('training'); // training or exam
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [showTip, setShowTip] = useState(true);
  const [streak, setStreak] = useState(0);
  const navigate = useNavigate();
  const { character: coach } = useRandomCharacter('grammar');

  // Tips de grammaire pour les personnages
  const grammarTips = [
    "La concordance des temps est la clé d'un texte fluide !",
    "Attention aux homophones : a/à, et/est, son/sont...",
    "Le participe passé s'accorde avec le COD placé avant !",
    "Un bon journaliste maîtrise l'orthographe sur le bout des doigts.",
    "Les phrases courtes sont plus percutantes.",
    "Évite les répétitions, utilise des synonymes !",
    "La ponctuation donne le rythme à ton texte."
  ];

  const [currentTip] = useState(grammarTips[Math.floor(Math.random() * grammarTips.length)]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/grammar/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const response = await api.get('/grammar/question', {
        params: selectedCategory ? { category: selectedCategory } : {}
      });
      setQuestion(response.data);
      setSelectedAnswer(null);
      setFeedback(null);
    } catch (error) {
      console.error('Error fetching question:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answerIndex) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answerIndex);
    setAttempts(attempts + 1);

    try {
      const response = await api.post('/grammar/answer', {
        questionId: question.id,
        answer: answerIndex,
        mode: mode
      });

      setFeedback(response.data);
      if (response.data.isCorrect) {
        setScore(score + 1);
        setStreak(streak + 1);
      } else {
        setStreak(0);
      }

      setTimeout(() => {
        fetchQuestion();
      }, mode === 'training' ? 4000 : 2000);
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <header className="border-b-4 border-black" style={{ background: '#E63946' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 comic-button bg-white text-black hover:bg-gray-100"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline" /> 
            <span className="hidden sm:inline">Retour Dashboard</span>
            <span className="sm:hidden">Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl comic-title text-white">
            <FaPencilAlt className="inline mr-2" />
            Grammaire
          </h1>
          <div className="text-white text-right">
            <p className="comic-text">Score: {score}/{attempts}</p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tip du coach */}
        <AnimatePresence>
          {showTip && coach && !question && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto mb-6"
            >
              <div className="speech-bubble p-4">
                <div className="flex items-start space-x-3">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-14 h-14 object-contain rounded-full border-2 border-black bg-white"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="font-bold comic-text" style={{ color: '#1B4965' }}>{coach.name} :</p>
                      <button 
                        onClick={() => setShowTip(false)}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >✕</button>
                    </div>
                    <p className="text-gray-700">
                      <FaLightbulb className="inline text-yellow-500 mr-1" />
                      {currentTip}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Streak indicator */}
        {streak >= 3 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="max-w-4xl mx-auto mb-4"
          >
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 rounded-lg text-center">
              <FaStar className="inline mr-2 animate-pulse" />
              <span className="font-bold">SÉRIE DE {streak} BONNES RÉPONSES !</span>
              <FaStar className="inline ml-2 animate-pulse" />
            </div>
          </motion.div>
        )}

        {!question ? (
          <div className="max-w-4xl mx-auto">
            {/* Mode Selection */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="comic-panel mb-6"
            >
              <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>Choisis ton mode</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setMode('training')}
                  className={`comic-panel p-6 cursor-pointer ${
                    mode === 'training' ? 'bg-green-100 border-green-500' : 'bg-white'
                  }`}
                  data-testid="training-mode-button"
                >
                  <FaBook className="text-5xl mx-auto mb-3" style={{ color: '#62B6CB' }} />
                  <h3 className="comic-text text-xl mb-2">ENTRAÎNEMENT</h3>
                  <p className="text-sm">Avec explications</p>
                </button>
                <button
                  onClick={() => setMode('exam')}
                  className={`comic-panel p-6 cursor-pointer ${
                    mode === 'exam' ? 'bg-red-100 border-red-500' : 'bg-white'
                  }`}
                  data-testid="exam-mode-button"
                >
                  <FaTrophy className="text-5xl mx-auto mb-3" style={{ color: '#FFB703' }} />
                  <h3 className="comic-text text-xl mb-2">EXAMEN</h3>
                  <p className="text-sm">Sans explications</p>
                </button>
              </div>
            </motion.div>

            {/* Category Selection */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="comic-panel mb-6"
            >
              <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>Catégorie (optionnel)</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`comic-button ${
                    selectedCategory === null ? 'comic-button-navy' : 'bg-gray-200'
                  }`}
                >
                  Toutes
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`comic-button ${
                      selectedCategory === cat ? 'comic-button-navy' : 'bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchQuestion}
              className="comic-button comic-button-red text-2xl py-4 w-full max-w-md mx-auto block"
              data-testid="start-training-button"
            >
              COMMENCER!
            </motion.button>
          </div>
        ) : (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-4xl mx-auto comic-panel"
          >
            <div className="mb-4">
              <span className="comic-badge bg-purple-500 text-white">{question.category}</span>
            </div>

            <h2 className="comic-text text-2xl mb-6" data-testid="question-text">
              {question.question}
            </h2>

            <div className="space-y-4" data-testid="answer-options">
              {question.options.map((option, index) => {
                let bgColor = 'bg-white';
                let borderColor = 'border-black';
                
                if (selectedAnswer !== null) {
                  if (index === question.correctAnswer) {
                    bgColor = 'bg-green-100';
                    borderColor = 'border-green-500';
                  } else if (index === selectedAnswer) {
                    bgColor = 'bg-red-100';
                    borderColor = 'border-red-500';
                  }
                }

                return (
                  <motion.button
                    key={index}
                    whileHover={selectedAnswer === null ? { scale: 1.02 } : {}}
                    onClick={() => handleAnswer(index)}
                    disabled={selectedAnswer !== null}
                    className={`w-full p-4 border-3 ${borderColor} ${bgColor} text-left transition-all ${
                      selectedAnswer === null ? 'hover:shadow-lg cursor-pointer' : 'cursor-not-allowed'
                    }`}
                    data-testid={`answer-option-${index}`}
                  >
                    <span className="comic-text text-lg">{option}</span>
                  </motion.button>
                );
              })}
            </div>

            {feedback && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="mt-6"
              >
                <QuizFeedbackBubble 
                  isCorrect={feedback.isCorrect}
                  feedback={feedback.feedback}
                  context="grammar"
                />

                {mode === 'training' && feedback.explanation && (
                  <div className="mt-4 p-4 bg-blue-50 border-3 border-blue-500 rounded">
                    <p className="text-sm"><strong>Explication:</strong> {feedback.explanation}</p>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default GrammarPage;
